import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'

import type { Notification } from '@/types/notification'
import { getBugsnag } from '@/utils/bugsnag.client'
import { streamKeys } from './stream.keys'
import {
  shouldRetryStreamBootstrapError,
  StreamBootstrapTransientError,
  toStreamBootstrapError,
} from '../streamBootstrap'

const STREAM_UNREAD_REFRESH_MS = 60_000

type OrderUnreadChannelV1 = {
  orderID: string
  unreadMessages: number
  lastMessageAt: string | null
  view: 'request' | 'seller'
}

type OrderUnreadResponseV1 = {
  complete: true
  items: OrderUnreadChannelV1[]
  totalUnreadMessages: number
  userID: string
}

function streamUnreadFailureDetails(error: unknown) {
  const cause = error instanceof Error ? error : undefined
  const httpStatus =
    cause && 'httpStatus' in cause && typeof cause.httpStatus === 'number'
      ? cause.httpStatus
      : undefined

  return { cause, errorName: cause?.name ?? 'UnknownError', httpStatus }
}

function reportStreamUnreadFailure(error: unknown) {
  const { cause, errorName, httpStatus } = streamUnreadFailureDetails(error)
  getBugsnag()?.notify(
    new Error('Stream unread synchronization failed', { cause }),
    (event) => {
      event.addMetadata('streamUnread', {
        scope: 'server-query',
        errorName,
        httpStatus,
      })
    },
  )
}

function parseUnreadResponse(payload: unknown): OrderUnreadResponseV1 {
  if (!payload || typeof payload !== 'object') {
    throw new StreamBootstrapTransientError()
  }

  const response = payload as Partial<OrderUnreadResponseV1>
  if (
    response.complete !== true ||
    typeof response.userID !== 'string' ||
    !Array.isArray(response.items) ||
    !Number.isSafeInteger(response.totalUnreadMessages)
  ) {
    throw new StreamBootstrapTransientError()
  }

  const items = response.items.map((item) => {
    if (
      !item ||
      typeof item.orderID !== 'string' ||
      !item.orderID ||
      !Number.isSafeInteger(item.unreadMessages) ||
      item.unreadMessages < 1 ||
      (item.lastMessageAt !== null && typeof item.lastMessageAt !== 'string') ||
      (item.view !== 'request' && item.view !== 'seller')
    ) {
      throw new StreamBootstrapTransientError()
    }
    return item
  })
  const totalUnreadMessages = items.reduce(
    (total, item) => total + item.unreadMessages,
    0,
  )
  if (totalUnreadMessages !== response.totalUnreadMessages) {
    throw new StreamBootstrapTransientError()
  }

  return {
    complete: true,
    items,
    totalUnreadMessages,
    userID: response.userID,
  }
}

async function fetchStreamOrderUnread(): Promise<OrderUnreadResponseV1> {
  let response: Response
  try {
    response = await fetch('/api/stream/unread', {
      cache: 'no-store',
    })
  } catch {
    throw new StreamBootstrapTransientError()
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      error?: string
    } | null
    throw Object.assign(
      toStreamBootstrapError(response.status, payload?.error),
      { httpStatus: response.status },
    )
  }

  return parseUnreadResponse(await response.json())
}

function unreadOrdersToNotifications(
  userID: string,
  unreadOrders: OrderUnreadChannelV1[],
): Notification[] {
  return unreadOrders.map((item) => ({
    id: `order-${item.orderID}`,
    key: `order-${item.orderID}-${userID}`,
    type: 'MESSAGE',
    userID,
    message:
      item.unreadMessages > 1
        ? `${item.unreadMessages} nouveaux messages sur une commande`
        : 'Nouveau message sur une commande',
    icon: 'ChatCenteredText',
    href: `/orders/${item.view}/message/${item.orderID}`,
    date: item.lastMessageAt ?? new Date().toISOString(),
    orderID: item.orderID,
  }))
}

export function useStreamUserUnread(userID: string | undefined) {
  const reportedErrorRef = useRef<string | undefined>(undefined)
  const { data, error } = useQuery({
    queryKey: streamKeys.userUnread(userID),
    queryFn: fetchStreamOrderUnread,
    enabled: Boolean(userID),
    retry: shouldRetryStreamBootstrapError,
    retryDelay: (attemptIndex) => 1500 * (attemptIndex + 1),
    refetchInterval: STREAM_UNREAD_REFRESH_MS,
    refetchOnWindowFocus: true,
    staleTime: STREAM_UNREAD_REFRESH_MS,
  })

  useEffect(() => {
    if (!error) {
      reportedErrorRef.current = undefined
      return
    }
    const { cause, errorName, httpStatus } = streamUnreadFailureDetails(error)
    const failureKey = JSON.stringify([
      userID,
      errorName,
      httpStatus,
      cause?.message,
    ])
    if (reportedErrorRef.current === failureKey) return

    reportedErrorRef.current = failureKey
    reportStreamUnreadFailure(error)
  }, [error, userID])

  return {
    unreadOrderNotifications: data
      ? unreadOrdersToNotifications(data.userID, data.items)
      : [],
    unreadCount: data?.totalUnreadMessages ?? 0,
    unreadError: error instanceof Error ? error : null,
  }
}
